import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Fab,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add,
  GridView,
  TableRows,
  CloudUpload,
  Refresh,
  Visibility,
  Download as DownloadIcon,
  Delete,
  PlayCircle,
  Transform,
  Close,
} from '@mui/icons-material';

import { useAuth, useRole } from '../../contexts/AuthContext';
import { convertMediaUrl } from '../../config/environment';
import { MediaType, MediaStatus } from "../../types";
import type { Media, SearchFilters, SortOptions, PaginatedResponse } from "../../types";
import apiClient from '../../services/api';
import MediaCard from '../../components/media/MediaCard';
import MediaFilters from '../../components/media/MediaFilters';
import UploadDialog from '../../components/media/UploadDialog';
import MediaViewer from '../../components/media/MediaViewer';
import { ConversionDialog } from '../../components/media';
import { invalidateMediaData } from '../../utils/cacheInvalidation';

const MediaPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { canUploadMedia, canEditMedia, canDeleteMedia } = useRole();
  const queryClient = useQueryClient();

  // Состояния для UI
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = viewMode === 'list' ? 50 : 12;
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Состояния для модальных окон
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<Media | null>(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<Media | null>(null);
  const [conversionDialogOpen, setConversionDialogOpen] = useState(false);
  const [mediaToConvert, setMediaToConvert] = useState<Media | null>(null);

  // Состояния для фильтрации и сортировки
  const [filters, setFilters] = useState<SearchFilters>({
    search: '',
    mediaTypes: [],
    statuses: [],
    tags: [],
  });

  const [sortOptions, setSortOptions] = useState<SortOptions>({
    field: 'created_at',
    direction: 'desc',
  });

  // Запрос медиафайлов
  const { data: mediaResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['media', currentPage, pageSize, filters, sortOptions],
    queryFn: async (): Promise<PaginatedResponse<Media>> => {
      return apiClient.getMedia(filters, sortOptions, currentPage, pageSize);
    },
    refetchInterval: 30000, // Обновляем каждые 30 секунд
  });

  // Мутация для удаления медиафайла
  const deleteMutation = useMutation({
    mutationFn: (mediaId: number) => apiClient.deleteMedia(mediaId),
    onSuccess: () => {
      invalidateMediaData(queryClient);
      setSnackbar({
        open: true,
        message: t('media.deleteSuccess'),
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || t('media.deleteError'),
        severity: 'error',
      });
    },
  });

  // Обработчики событий
  const handleFiltersChange = (newFilters: SearchFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтров
  };

  const handleSortChange = (newSort: SortOptions) => {
    setSortOptions(newSort);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      mediaTypes: [],
      statuses: [],
      tags: [],
    });
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    // Прокручиваем к началу страницы
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newViewMode: 'grid' | 'list') => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      setCurrentPage(1);
    }
  };

  // Действия с медиафайлами
  const handleMediaView = (media: Media) => {
    setSelectedMedia(media);
    setViewerOpen(true);
  };

  const handleMediaEdit = (media: Media) => {
    console.log('Редактирование медиафайла:', media);
    // TODO: Открыть модальное окно редактирования
    setSnackbar({
      open: true,
      message: t('media.editNotImplemented'),
      severity: 'info',
    });
  };

  const handleMediaDelete = (media: Media) => {
    setMediaToDelete(media);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (mediaToDelete) {
      await deleteMutation.mutateAsync(mediaToDelete.id);
      setDeleteDialogOpen(false);
      setMediaToDelete(null);
    }
  };

  const handleMediaDownload = (media: Media) => {
    const downloadUrl = apiClient.getMediaDownloadUrl(media.id);
    window.open(downloadUrl, '_blank');
  };

  const handleMediaPreview = (media: Media) => {
    setPreviewMedia(media);
    setPreviewOpen(true);
  };

  const handleMediaConvert = (media: Media) => {
    setMediaToConvert(media);
    setConversionDialogOpen(true);
  };

  const handleUpload = () => {
    setUploadDialogOpen(true);
  };

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    if (!previewOpen || !previewMedia) {
      setPreviewObjectUrl(null);
      setPreviewLoading(false);
      setPreviewError(null);
      return undefined;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    apiClient.getMediaBlob(previewMedia.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewObjectUrl(objectUrl);
      })
      .catch((error) => {
        if (cancelled) return;
        setPreviewError(error?.detail || error?.message || 'Не удалось загрузить файл для предпросмотра');
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [previewOpen, previewMedia]);

  const handleUploadComplete = async () => {
    setUploadDialogOpen(false);
    setCurrentPage(1);
    await invalidateMediaData(queryClient);
    setSnackbar({
      open: true,
      message: t('media.uploadCompleted'),
      severity: 'success',
    });
  };

  const formatFileSize = (bytes?: number | null) => {
    if (!bytes) return '-';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  };

  const formatDuration = (seconds?: number | null) => {
    if (!seconds) return '-';
    const total = Math.round(seconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    return hours > 0
      ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      : `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: MediaStatus | string) => {
    switch (status) {
      case MediaStatus.READY:
        return 'success';
      case MediaStatus.PROCESSING:
      case MediaStatus.UPLOADING:
        return 'warning';
      case MediaStatus.ERROR:
        return 'error';
      default:
        return 'default';
    }
  };

  const getMediaKind = (media: Media | null) => media?.media_type || media?.type;

  const getPreviewUrl = (media: Media) => {
    return previewObjectUrl || (media.file_url ? convertMediaUrl(media.file_url) : apiClient.getMediaDownloadUrl(media.id));
  };

  const renderQuickPreview = () => {
    if (!previewMedia) return null;

    const kind = getMediaKind(previewMedia);
    const previewUrl = getPreviewUrl(previewMedia);

    if (previewLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 260 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (previewError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {previewError}
        </Alert>
      );
    }

    if (kind === MediaType.VIDEO) {
      return (
        <video
          controls
          autoPlay
          style={{ width: '100%', maxHeight: '72vh', background: '#000', display: 'block' }}
          poster={previewMedia.thumbnail_url ? convertMediaUrl(previewMedia.thumbnail_url) : undefined}
        >
          <source src={previewUrl} type={previewMedia.mime_type || 'video/mp4'} />
        </video>
      );
    }

    if (kind === MediaType.AUDIO) {
      return (
        <Box sx={{ p: 3 }}>
          <audio controls autoPlay style={{ width: '100%' }}>
            <source src={previewUrl} type={previewMedia.mime_type || 'audio/mpeg'} />
          </audio>
        </Box>
      );
    }

    if (kind === MediaType.IMAGE) {
      return (
        <Box
          component="img"
          src={previewUrl}
          alt={previewMedia.title}
          sx={{ display: 'block', maxWidth: '100%', maxHeight: '72vh', objectFit: 'contain', mx: 'auto' }}
        />
      );
    }

    if (previewMedia.mime_type === 'application/pdf') {
      return (
        <Box
          component="iframe"
          src={previewUrl}
          title={previewMedia.title}
          sx={{ width: '100%', height: '72vh', border: 0 }}
        />
      );
    }

    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Быстрый предпросмотр для этого типа файла недоступен.
        </Typography>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => handleMediaDownload(previewMedia)}>
          Скачать файл
        </Button>
      </Box>
    );
  };

  // Загрузочные скелетоны
  const renderSkeletons = () => (
    viewMode === 'list' ? (
      <Paper elevation={1}>
        <Box sx={{ p: 1 }}>
          {Array.from({ length: 12 }).map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={36} sx={{ mb: 0.5 }} />
          ))}
        </Box>
      </Paper>
    ) : (
      <Grid container spacing={2}>
        {Array.from({ length: pageSize }).map((_, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
            <Paper elevation={1} sx={{ overflow: 'hidden' }}>
              <Skeleton variant="rectangular" height={200} />
              <Box sx={{ p: 2 }}>
                <Skeleton variant="text" height={24} />
                <Skeleton variant="text" height={16} width="60%" />
                <Skeleton variant="text" height={16} width="40%" />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    )
  );

  // Отображение медиафайлов
  const renderMediaGrid = () => {
    if (!mediaResponse?.items.length) {
      return (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('media.noFiles')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('media.noFilesDescription')}
          </Typography>
          {canUploadMedia() && (
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={handleUpload}
            >
              {t('media.uploadFirst')}
            </Button>
          )}
        </Paper>
      );
    }

    return (
      <Grid container spacing={2}>
        {mediaResponse.items.map((media) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={media.id}>
            <MediaCard
              media={media}
              onView={handleMediaView}
              onEdit={canEditMedia() ? handleMediaEdit : undefined}
              onDelete={canDeleteMedia() ? handleMediaDelete : undefined}
              onDownload={handleMediaDownload}
              onPreview={handleMediaPreview}
              onConvert={handleMediaConvert}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderMediaTable = () => {
    if (!mediaResponse?.items.length) {
      return renderMediaGrid();
    }

    return (
      <TableContainer component={Paper} elevation={1} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small" stickyHeader sx={{ minWidth: 1500, '& td, & th': { py: 0.55, px: 0.75, fontSize: 12 } }}>
          <TableHead>
            <TableRow>
              <TableCell width={64}>ID</TableCell>
              <TableCell width={52}>Тип</TableCell>
              <TableCell>Название / файл</TableCell>
              <TableCell width={120}>Статус</TableCell>
              <TableCell width={120}>Файл</TableCell>
              <TableCell width={92}>Размер</TableCell>
              <TableCell width={92}>Длит.</TableCell>
              <TableCell width={120}>Разрешение</TableCell>
              <TableCell width={150}>MIME / ext</TableCell>
              <TableCell width={180}>Теги</TableCell>
              <TableCell width={90}>Автор</TableCell>
              <TableCell width={150}>Создан</TableCell>
              <TableCell width={150}>Обновлен</TableCell>
              <TableCell width={150} align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mediaResponse.items.map((media) => {
              const resolution = media.resolution || (media.width && media.height ? `${media.width}x${media.height}` : '-');
              const fileState = media.file_exists === false
                ? { label: 'Нет файла', color: 'error' as const }
                : media.is_playable === false
                  ? { label: 'Не playable', color: 'warning' as const }
                  : { label: media.availability_status || 'OK', color: 'success' as const };
              const canConvert = media.media_type === MediaType.VIDEO
                || media.media_type === MediaType.AUDIO
                || media.type === MediaType.VIDEO
                || media.type === MediaType.AUDIO;

              return (
                <TableRow key={media.id} hover>
                  <TableCell sx={{ color: 'text.secondary' }}>#{media.id}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={(media.media_type || media.type || 'unknown').toUpperCase()} sx={{ height: 20, fontSize: 10 }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 260, maxWidth: 420 }}>
                      <Typography variant="body2" fontWeight={600} noWrap title={media.title}>
                        {media.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div" title={media.original_filename || media.filename}>
                        {media.original_filename || media.filename}
                      </Typography>
                      {media.description && (
                        <Typography variant="caption" color="text.secondary" noWrap component="div" title={media.description}>
                          {media.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={media.status} color={getStatusColor(media.status) as any} sx={{ height: 20, fontSize: 10 }} />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={media.availability_message || ''}>
                      <Chip size="small" label={fileState.label} color={fileState.color} variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatFileSize(media.file_size)}</TableCell>
                  <TableCell>{formatDuration(media.duration)}</TableCell>
                  <TableCell>{resolution}</TableCell>
                  <TableCell>
                    <Typography variant="caption" component="div" noWrap title={media.mime_type || '-'}>
                      {media.mime_type || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {media.file_extension || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ maxWidth: 180 }}>
                      {(media.tags || []).slice(0, 4).map((tag) => (
                        <Chip key={tag} size="small" label={tag} variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                      ))}
                      {(media.tags || []).length > 4 && (
                        <Chip size="small" label={`+${(media.tags || []).length - 4}`} sx={{ height: 18, fontSize: 10 }} />
                      )}
                      {(!media.tags || media.tags.length === 0) && <Typography variant="caption" color="text.secondary">-</Typography>}
                    </Stack>
                  </TableCell>
                  <TableCell>{media.uploaded_by || '-'}</TableCell>
                  <TableCell>{media.created_at ? new Date(media.created_at).toLocaleString() : '-'}</TableCell>
                  <TableCell>{media.updated_at ? new Date(media.updated_at).toLocaleString() : '-'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                      <Tooltip title="Открыть">
                        <IconButton size="small" onClick={() => handleMediaView(media)}>
                          <Visibility fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      {([MediaType.VIDEO, MediaType.AUDIO, MediaType.IMAGE] as string[]).includes(String(media.media_type || media.type || '')) && (
                        <Tooltip title="Предпросмотр">
                          <IconButton size="small" onClick={() => handleMediaPreview(media)}>
                            <PlayCircle fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Скачать">
                        <IconButton size="small" onClick={() => handleMediaDownload(media)}>
                          <DownloadIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      {canConvert && (
                        <Tooltip title="Конвертировать">
                          <IconButton size="small" onClick={() => handleMediaConvert(media)}>
                            <Transform fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDeleteMedia() && (
                        <Tooltip title="Удалить">
                          <IconButton size="small" color="error" onClick={() => handleMediaDelete(media)}>
                            <Delete fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      {/* Заголовок страницы */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            {t('navigation.media')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('media.pageDescription')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Переключатель вида */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="grid" aria-label="grid view">
              <GridView />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <TableRows />
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Кнопка обновления */}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            {t('common.refresh')}
          </Button>

          {/* Кнопка загрузки */}
          {canUploadMedia() && (
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={handleUpload}
            >
              {t('common.upload')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Фильтры */}
      <MediaFilters
        filters={filters}
        sortOptions={sortOptions}
        onFiltersChange={handleFiltersChange}
        onSortChange={handleSortChange}
        onClearFilters={handleClearFilters}
        totalItems={mediaResponse?.total || 0}
      />

      {/* Ошибка загрузки */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('common.loadError')}
        </Alert>
      )}

      {/* Контент страницы */}
      {isLoading ? renderSkeletons() : (viewMode === 'list' ? renderMediaTable() : renderMediaGrid())}

      {/* Пагинация */}
      {mediaResponse && mediaResponse.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={mediaResponse.total_pages}
            page={currentPage}
            onChange={handlePageChange}
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* FAB для мобильных устройств */}
      {canUploadMedia() && (
        <Fab
          color="primary"
          aria-label="upload"
          onClick={handleUpload}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Диалог загрузки */}
      <UploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUploadComplete={handleUploadComplete}
      />

      {/* Просмотрщик медиафайлов */}
      <MediaViewer
        media={selectedMedia}
        open={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setSelectedMedia(null);
        }}
        onEdit={canEditMedia() ? handleMediaEdit : undefined}
        onDelete={canDeleteMedia() ? handleMediaDelete : undefined}
      />

      <Dialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewMedia(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="h6" noWrap title={previewMedia?.title}>
            Предпросмотр: {previewMedia?.title}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => {
              setPreviewOpen(false);
              setPreviewMedia(null);
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: 'background.default' }}>
          {renderQuickPreview()}
        </DialogContent>
        <DialogActions>
          {previewMedia && (
            <Button
              onClick={() => {
                handleMediaView(previewMedia);
                setPreviewOpen(false);
                setPreviewMedia(null);
              }}
              startIcon={<Visibility />}
            >
              Открыть карточку
            </Button>
          )}
          <Button
            onClick={() => {
              setPreviewOpen(false);
              setPreviewMedia(null);
            }}
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('media.confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('media.confirmDeleteMessage', { title: mediaToDelete?.title })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={24} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>



      {/* Диалог конвертации */}
      <ConversionDialog
        open={conversionDialogOpen}
        onClose={() => setConversionDialogOpen(false)}
        media={mediaToConvert}
      />
    </Box>
  );
};

export default MediaPage;
